import { useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import Fab from '@material-ui/core/Fab'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

import usePhotoViewerNavigation from 'photo-module/photo/core/usePhotoViewerNavigation'
import useSelectedPhoto from 'photo-module/photo/core/useSelectedPhoto'
import useKeyPress from 'shared-module/components/key-press/useKeyPress'
import VoskerButtonWrapper from './VoskerButtonWrapper'
import NextArrowIcon from '../icons/viewer/NextArrowIcon'

const useStyles = makeStyles(theme => ({
  next: {
    display: 'flex',
    position: 'absolute',
    height: 'calc(100vh - 128px)',
    width: '40vw',
    alignItems: 'center',
    transition: 'opacity 0.2s ease',
    cursor: 'ew-resize',
    opacity: 0,
    right: '20px',
    marginRight: '-20px',
    paddingRight: '20px',
    justifyContent: 'flex-end',
    '&:hover': {
      opacity: 1,
    },
    [theme.breakpoints.down('xs')]: {
      height: '20%',
    },
  },
  arrow: {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.background.tile,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      boxShadow: 'none',
    },
  },
  icon: {
    marginLeft: 2,
  },
}))

/**
 * Creates the computed styles for the button container based on custom style props
 * @param {Object} customStyle - Custom styles passed as props
 * @returns {Object} Computed styles object
 */
const createButtonStyles = (customStyle) => {
  const hasCustomStyles = Object.keys(customStyle).length > 0

  return {
    ...customStyle,
    ...(hasCustomStyles && {
      position: 'relative',
      opacity: 1,
      right: 'auto',
      marginRight: 0,
      paddingRight: 0,
      width: 'auto',
      height: 'auto',
      justifyContent: 'center',
    }),
  }
}

const NextButton = ({ style = {}, className = '' }) => {
  const classes = useStyles()
  const arrowRightPressed = useKeyPress('ArrowRight')
  const photo = useSelectedPhoto()
  const { nextPhoto, switchPhoto } = usePhotoViewerNavigation(photo?.id)

  const handleNext = useCallback(() => {
    switchPhoto(nextPhoto)
  }, [switchPhoto, nextPhoto])

  const buttonStyles = useMemo(() => createButtonStyles(style), [style])

  const classNames = useMemo(() =>
    `${classes.next} ${className}`.trim(),
  [classes.next, className],
  )

  useEffect(() => {
    if (arrowRightPressed && nextPhoto) {
      handleNext()
    }
  }, [arrowRightPressed, nextPhoto, handleNext])

  const renderButtonContent = () => {
    const IconComponent = ChevronRightIcon

    return (
      <Fab className={classes.arrow} color="secondary" disabled={!nextPhoto}>
        <IconComponent className={classes.icon} fontSize="large" />
      </Fab>
    )
  }

  return (
    <Box
      onClick={nextPhoto ? handleNext : undefined}
      className={classNames}
      style={buttonStyles}
      role="button"
      aria-label="Next photo"
      tabIndex={nextPhoto ? 0 : -1}
      onKeyDown={(e) => {
        if (nextPhoto && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          handleNext()
        }
      }}
    >
      { renderButtonContent() }
    </Box>
  )
}

NextButton.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
}

export default NextButton
